import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';

import logo from 'assets/logos/r.World-company-logo.png';
import { PurchaseOrderPdfData } from './PurchaseOrderPdf.types';
import SkuTableHeadRow from './components/PurchaseOrder/SkuTableHeadRow';
import SkuTableRow from './components/PurchaseOrder/SkuTableRow';

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#333333',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  logo: {
    width: 120,
    height: 40,
    objectFit: 'contain',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  section: {
    marginBottom: 14,
  },
  twoColumnRow: {
    flexDirection: 'row',
    marginBottom: 14,
  },
  halfColumnLeft: {
    flex: 1,
    paddingRight: 5,
  },
  halfColumnRight: {
    flex: 1,
    paddingLeft: 5,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#075015',
  },
  row: {
    flexDirection: 'row',
    paddingLeft: 6,
    marginBottom: 4,
  },
  label: {
    width: 120,
    fontWeight: 'bold',
  },
  value: {
    flex: 1,
  },
  table: {
    borderWidth: 1,
    borderColor: '#4CAF50',
    marginTop: 8,
  },
  footer: {
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  subtotal: {
    fontSize: 12,
    fontWeight: 'bold',
  },
});

type Props = {
  data: PurchaseOrderPdfData;
};

const formatMoney = (value: number) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const InfoRow = ({ label, value }: { label?: string; value: string }) => (
  <View style={styles.row}>
    {label ? <Text style={styles.label}>{label}</Text> : null}
    <Text style={styles.value}>{value || '-'}</Text>
  </View>
);

const ManufacturerAddressRows = ({
  line1,
  line2,
  city,
  state,
  zip,
}: {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}) => {
  const cityStateZip = [city, state, zip].filter(Boolean).join(', ');

  return (
    <View style={styles.row}>
      <Text style={styles.label}>Address</Text>
      <View style={styles.value}>
        <Text>{line1 || '-'}</Text>
        {line2 ? <Text>{line2}</Text> : null}
        <Text>{cityStateZip || '-'}</Text>
      </View>
    </View>
  );
};

const PurchaseOrderPdfTemplate = ({ data }: Props) => (
  <Document>
    <Page size="LETTER" style={styles.page}>
      <View style={styles.header}>
        <Image style={styles.logo} src={logo} />
        <View>
          <Text style={styles.title}>Purchase Order</Text>
          <Text>PO#: {data.poNumber || '-'}</Text>
          <Text>Date: {data.date || '-'}</Text>
        </View>
      </View>

      <View style={styles.twoColumnRow}>
        <View style={styles.halfColumnLeft}>
          <Text style={styles.sectionTitle}>Vendor</Text>
          <InfoRow label="Name" value={data.manufacturerName} />
          <ManufacturerAddressRows
            line1={data.manufacturerAddressLine1}
            line2={data.manufacturerAddressLine2}
            city={data.manufacturerCity}
            state={data.manufacturerState}
            zip={data.manufacturerZip}
          />
          <InfoRow label="Contact" value={data.manufacturerContact} />
          <Text style={{ ...styles.sectionTitle, marginTop: 10 }}>Ship To</Text>
          <InfoRow value="r.World PBC" />
          <InfoRow value={data.shipToAddressLine1} />
          <InfoRow value={data.shipToAddressLine2} />
          <InfoRow
            value={[data.shipToCity, data.shipToState, data.shipToZip]
              .filter(Boolean)
              .join(', ')}
          />
        </View>

        <View style={styles.halfColumnRight}>
          <Text style={{ ...styles.sectionTitle, marginTop: 10 }}>
            Shipping Contact
          </Text>
          <InfoRow value={data.shipToContact} />
          {data.shipToContactPhone ? (
            <>
              <InfoRow value={data.shipToContactPhone} />
              <Text style={{ paddingLeft: 6 }}>Call to schedule delivery</Text>
            </>
          ) : null}

          {data.liftGateRequired && (
            <>
              <Text style={{ ...styles.sectionTitle, marginTop: 10 }}>
                Lift Gate
              </Text>
              <InfoRow value="Lift Gate Required" />
            </>
          )}
          {data.shippingNotes && data.shippingNotes.length > 0 && (
            <>
              <Text style={{ ...styles.sectionTitle, marginTop: 10 }}>
                Shipping Notes
              </Text>
              <Text style={{ paddingLeft: 6 }}>{data.shippingNotes}</Text>
            </>
          )}
        </View>
      </View>

      <View style={{ ...styles.section, marginTop: 10 }}>
        <View style={styles.table}>
          <SkuTableHeadRow />
          {data.skuLines.map((line, index) => (
            <SkuTableRow key={index} line={line} isOdd={index % 2 === 0} />
          ))}
        </View>
      </View>

      <View style={styles.footer}>
        <Text style={styles.subtotal}>
          Subtotal: {formatMoney(data.subtotal)}
        </Text>
        <Text style={styles.subtotal}>
          Shipping: {formatMoney(data.shippingCost)}
        </Text>
        <Text style={styles.subtotal}>Tax: {formatMoney(data.tax)}</Text>
        <Text style={styles.subtotal}>
          Adjustment: {formatMoney(data.adjustment)}
        </Text>
        <Text style={styles.subtotal}>
          Total:{' '}
          {formatMoney(
            data.subtotal + data.shippingCost + data.tax + data.adjustment,
          )}
        </Text>
      </View>
    </Page>
  </Document>
);

export default PurchaseOrderPdfTemplate;
